import { Menu, Search, Bell, ZoomIn, ZoomOut, Sparkles, Sun, Moon, Monitor } from 'lucide-react';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { CommandPalette } from './CommandPalette';
import { usePrefs } from '@/lib/prefs';

export const Topbar = ({ breadcrumbs = [], onOpenMobileSidebar }) => {
  const nav = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const { prefs, patch } = usePrefs();

  const refreshUnread = useCallback(async () => {
    try { const r = await api.get('/notifications/count'); setUnread(r.data.unread || 0); } catch {}
  }, []);
  useEffect(() => { refreshUnread(); const t = setInterval(refreshUnread, 15000); return () => clearInterval(t); }, [refreshUnread]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault(); setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const zoomTick = (delta) => {
    const next = Math.max(85, Math.min(125, (prefs.zoom || 100) + delta));
    patch({ zoom: next });
  };

  return (
    <>
      <div className="h-14 border-b flex items-center gap-3 px-4 sm:px-6 sticky top-0 z-30 backdrop-blur"
           style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-0) / 0.9)' }}>
        <button className="lg:hidden p-1 rounded hover:bg-[hsl(var(--surface-2))]" onClick={onOpenMobileSidebar}>
          <Menu size={18} />
        </button>
        <div className="flex items-center gap-2 text-[13px] text-[hsl(var(--foreground-muted))] min-w-0 flex-1 truncate"
             data-testid="topbar-breadcrumb">
          {breadcrumbs.map((b, i) => (
            <span key={i} className="flex items-center gap-2 truncate">
              {i > 0 && <span className="text-[hsl(var(--foreground-subtle))]">/</span>}
              {b.to ? (
                <button onClick={() => nav(b.to)} className="hover:underline truncate">{b.label}</button>
              ) : (
                <span className={i === breadcrumbs.length - 1 ? 'text-[hsl(var(--foreground))] font-medium truncate' : 'truncate'}>
                  {b.label}
                </span>
              )}
            </span>
          ))}
        </div>

        <button
          onClick={() => setPaletteOpen(true)}
          data-testid="topbar-command-palette-trigger"
          className="hidden md:flex items-center gap-2 border rounded-lg px-3 h-8 text-[13px] text-[hsl(var(--foreground-subtle))] hover:border-[hsl(var(--border-strong))]"
          style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-0))' }}
        >
          <Search size={14} />
          <span>Search or jump to…</span>
          <span className="ml-2 font-mono text-[10.5px] px-1.5 py-0.5 rounded bg-[hsl(var(--surface-2))] text-[hsl(var(--foreground-muted))]">⌘ K</span>
        </button>
        <button className="md:hidden p-1.5 rounded hover:bg-[hsl(var(--surface-2))]" onClick={() => setPaletteOpen(true)} aria-label="Search">
          <Search size={16} />
        </button>

        <div className="hidden sm:flex items-center gap-0 border rounded-md h-8 desktop-only" style={{ borderColor: 'hsl(var(--border))' }} data-testid="topbar-zoom">
          <button onClick={() => zoomTick(-5)} className="px-1.5 h-8 hover:bg-[hsl(var(--surface-2))] rounded-l-md" data-testid="topbar-zoom-out" aria-label="Zoom out">
            <ZoomOut size={14} />
          </button>
          <div className="px-2 text-[11.5px] font-mono text-[hsl(var(--foreground-muted))] tabular-nums w-11 text-center border-x" style={{ borderColor: 'hsl(var(--border))' }} data-testid="topbar-zoom-value">{prefs.zoom || 100}%</div>
          <button onClick={() => zoomTick(5)} className="px-1.5 h-8 hover:bg-[hsl(var(--surface-2))] rounded-r-md" data-testid="topbar-zoom-in" aria-label="Zoom in">
            <ZoomIn size={14} />
          </button>
        </div>

        <button onClick={() => nav('/settings')} className="hidden sm:inline-flex p-1.5 rounded hover:bg-[hsl(var(--surface-2))] desktop-only" title="Personalize" data-testid="topbar-personalize">
          <Sparkles size={16} />
        </button>

        <button
          onClick={() => {
            const cur = prefs.theme || 'system';
            const next = cur === 'light' ? 'dark' : cur === 'dark' ? 'system' : 'light';
            patch({ theme: next });
          }}
          className="p-1.5 rounded hover:bg-[hsl(var(--surface-2))]"
          title={`Theme: ${prefs.theme || 'system'} (click to cycle)`}
          data-testid="topbar-theme-toggle"
        >
          {(prefs.theme || 'system') === 'light' && <Sun size={16} />}
          {(prefs.theme || 'system') === 'dark' && <Moon size={16} />}
          {(prefs.theme || 'system') === 'system' && <Monitor size={16} />}
        </button>

        <button
          data-testid="topbar-notifications-button"
          onClick={() => nav('/notifications')}
          className="relative p-1.5 rounded hover:bg-[hsl(var(--surface-2))]"
          aria-label="Notifications"
        >
          <Bell size={17} />
          {unread > 0 && (
            <span data-testid="topbar-notifications-unread-badge"
                  className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-semibold flex items-center justify-center text-white"
                  style={{ background: 'hsl(var(--danger))' }}>{unread}</span>
          )}
        </button>
      </div>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </>
  );
};
